import { Suspense, lazy } from 'react';
import includes from 'lodash/includes';
import { Route, Routes, useLocation } from 'react-router-dom';
import { Navigate } from 'react-router-dom';
import { AuthRoute } from '../components/AuthRoute';
import { Footer } from '../components/Footer';
import { DocumentReminderModal } from '../components/modal/DocumentReminderModal';
import { Modal } from '../components/modal/Modal';
import { MainNavBar } from '../components/navbar/MainNavBar';
import { TopNavBar } from '../components/navbar/TopNavBar';
import { SHOW_BOTTOM_NAVBAR_ROUTES } from './constants/router';
import { HomePage } from './home/HomePage';

// 홈(/)만 첫 JS에 넣고, 나머지 공개 화면은 들어갈 때 받는다.
// 홈은 첫 진입이 가장 많고 영상이 있어 청크 요청을 한 단계 더 늘리지 않는다.
// 달력·결제처럼 특정 화면만 쓰는 라이브러리는 그 화면 덩어리로만 간다.
// 상단·하단 바는 화면을 받는 동안에도 남도록 Suspense를 <main> 안에 둔다.
// fallback이 비면 푸터가 화면 안으로 올라왔다가 내용이 들어오며 밀려(CLS) 한 화면 높이를 잡아 둔다.
const AlimtalkActionPage = lazy(() => import('./alimtalk/AlimtalkActionPage'));
const ContractAddPage = lazy(() =>
  import('./contract/ContractAddPage').then((m) => ({
    default: m.ContractAddPage,
  }))
);
const ContractDetailPage = lazy(() =>
  import('./contract/[id]/ContractDetailPage').then((m) => ({
    default: m.ContractDetailPage,
  }))
);
const EventListPage = lazy(() =>
  import('./event/EventListPage').then((m) => ({ default: m.EventListPage }))
);
const EventDetailPage = lazy(() =>
  import('./event/[id]/EventDetailPage').then((m) => ({
    default: m.EventDetailPage,
  }))
);
const FindPasswordPage = lazy(() =>
  import('./find/password/FindPasswordPage').then((m) => ({
    default: m.FindPasswordPage,
  }))
);
const ResetPasswordSuccessPage = lazy(() =>
  import('./find/password/Success/ResetPasswordSuccessPage').then((m) => ({
    default: m.ResetPasswordSuccessPage,
  }))
);
const ResetPasswordPage = lazy(() =>
  import('./find/password/reset/ResetPasswordPage').then((m) => ({
    default: m.ResetPasswordPage,
  }))
);
const SendEmailPage = lazy(() =>
  import('./find/password/send-email/SendEmailPage').then((m) => ({
    default: m.SendEmailPage,
  }))
);
const GiftconBrandDetailPage = lazy(
  () => import('./giftcon-brands/GiftconBrandDetailPage')
);
const GiftconBrandListPage = lazy(
  () => import('./giftcon-brands/GiftconBrandListPage')
);
const GiftconProductDetail = lazy(
  () => import('./giftcon-brands/products/GiftconProductDetail')
);
const LoginPage = lazy(() =>
  import('./login/LoginPage').then((m) => ({ default: m.LoginPage }))
);
const MainPage = lazy(() =>
  import('./main/MainPage').then((m) => ({ default: m.MainPage }))
);
const MyPage = lazy(() =>
  import('./mypage/MyPage').then((m) => ({ default: m.MyPage }))
);
const EditPage = lazy(() =>
  import('./mypage/edit/EditPage').then((m) => ({ default: m.EditPage }))
);
const MyGiftconDetail = lazy(() => import('./mypage/giftcons/MyGiftconDetail'));
const MyGiftcons = lazy(() => import('./mypage/giftcons/MyGiftcons'));
const HistoryPage = lazy(() =>
  import('./mypage/history/HistoryPage').then((m) => ({
    default: m.HistoryPage,
  }))
);
const LikePage = lazy(() =>
  import('./mypage/like/LikePage').then((m) => ({ default: m.LikePage }))
);
const MyPointHistories = lazy(
  () => import('./mypage/point-history/MyPointHistories')
);
const ResumeEditPage = lazy(() =>
  import('./mypage/resume-edit/ResumeEditPage').then((m) => ({
    default: m.ResumeEditPage,
  }))
);
const MyWithdrawal = lazy(() => import('./mypage/withdrawal/MyWithdrawal'));
const NoticePage = lazy(() =>
  import('./notice/NoticePage').then((m) => ({ default: m.NoticePage }))
);
const NoticeDetailPage = lazy(() =>
  import('./notice/[id]/NoticeDetailPage').then((m) => ({
    default: m.NoticeDetailPage,
  }))
);
const PartTimeJobPage = lazy(() =>
  import('./part-time-job/PartTimeJobPage').then((m) => ({
    default: m.PartTimeJobPage,
  }))
);
const PartTimeJobDetailPage = lazy(() =>
  import('./part-time-job/[id]/PartTimeJobDetailPage').then((m) => ({
    default: m.PartTimeJobDetailPage,
  }))
);
const PartTimeJobApplyPage = lazy(() =>
  import('./part-time-job/apply/PartTimeJobApplyPage').then((m) => ({
    default: m.PartTimeJobApplyPage,
  }))
);
const PaymentRequestPage = lazy(() =>
  import('./payment-request/PaymentRequestPage').then((m) => ({
    default: m.PaymentRequestPage,
  }))
);
const PaymentRequestDetailPage = lazy(() =>
  import('./payment-request/[id]/PaymentRequestDetailPage').then((m) => ({
    default: m.PaymentRequestDetailPage,
  }))
);
const ResumeAddPage = lazy(() =>
  import('./resume/ResumeAddPage').then((m) => ({ default: m.ResumeAddPage }))
);
const SignupPage = lazy(() =>
  import('./signup/SignupPage').then((m) => ({ default: m.SignupPage }))
);
const SignUpSuccessPage = lazy(() =>
  import('./signup/success/SignUpSuccessPage').then((m) => ({
    default: m.SignUpSuccessPage,
  }))
);

export const Router = () => {
  const { pathname } = useLocation();
  const isWhitePage =
    pathname === '/login' ||
    pathname.startsWith('/part-time-job') ||
    pathname.startsWith('/signup/success') ||
    pathname.startsWith('/signup') ||
    pathname.startsWith('/find/password/send-email') ||
    pathname.startsWith('/find-password') ||
    pathname.startsWith('/find-password/reset') ||
    pathname.startsWith('/part-time-job/:id/apply') ||
    pathname.startsWith('/part-time-job/:id') ||
    pathname.startsWith('/payment-request/:id') ||
    pathname.startsWith('/payment-request') ||
    pathname.startsWith('/contract/:id') ||
    pathname.startsWith('/contract/add') ||
    pathname.startsWith('/reset-password/:resetToken') ||
    pathname.startsWith('/reset-password/success') ||
    pathname.startsWith('/resume/:resumeId') ||
    pathname.startsWith('/mypage/resume') ||
    pathname.startsWith('/mypage/edit') ||
    pathname.startsWith('/mypage') ||
    pathname.startsWith('/mypage/history') ||
    pathname.startsWith('/mypage/like') ||
    pathname.startsWith('/notice/:id') ||
    pathname.startsWith('/notice') ||
    pathname.startsWith('/event/:id') ||
    pathname.startsWith('/event') ||
    pathname.startsWith('/giftcon-brands') ||
    pathname.startsWith('/alimtalk');

  return (
    <>
      {pathname === '/' ? (
        <MainNavBar />
      ) : (
        !includes(SHOW_BOTTOM_NAVBAR_ROUTES, pathname) && (
          <TopNavBar isWhite={isWhitePage} />
        )
      )}

      <Modal />
      <DocumentReminderModal />

      <div className="flex min-h-screen flex-1 flex-col">
        <main className="relative flex flex-1 flex-col">
          <Suspense fallback={<div className="min-h-screen" />}>
            <Routes>
              <Route path="/signup/success" element={<SignUpSuccessPage />} />
              <Route
                path="/signup"
                element={<AuthRoute element={<SignupPage />} guestOnly />}
              />

              <Route path="/login" element={<LoginPage />} />

              <Route
                path="/giftcon-brands/:id/products/:giftconProductId"
                element={<GiftconProductDetail />}
              />
              <Route
                path="/giftcon-brands/:id"
                element={<GiftconBrandDetailPage />}
              />
              <Route
                path="/giftcon-brands"
                element={<GiftconBrandListPage />}
              />

              <Route
                path="/find-password/send-email"
                element={<SendEmailPage />}
              />
              <Route path="/find-password" element={<FindPasswordPage />} />
              <Route
                path="/part-time-job/:id/apply"
                element={<PartTimeJobApplyPage />}
              />
              <Route
                path="/part-time-job/:id"
                element={<PartTimeJobDetailPage />}
              />
              <Route path="/part-time-job" element={<PartTimeJobPage />} />

              <Route
                path="/payment-request/:id"
                element={<PaymentRequestDetailPage />}
              />
              <Route path="/payment-request" element={<PaymentRequestPage />} />

              <Route path="/contract/:id" element={<ContractDetailPage />} />
              <Route path="/contract/add" element={<ContractAddPage />} />

              <Route
                path="/reset-password/success"
                element={<ResetPasswordSuccessPage />}
              />
              <Route
                path="/reset-password/:resetToken"
                element={<ResetPasswordPage />}
              />

              <Route path="/resume/:resumeId" element={<ResumeAddPage />} />
              <Route path="/mypage/resume" element={<ResumeEditPage />} />

              <Route
                path="/mypage/giftcons/:id"
                element={<MyGiftconDetail />}
              />
              <Route path="/mypage/giftcons" element={<MyGiftcons />} />

              <Route
                path="/mypage/point-history"
                element={<MyPointHistories />}
              />

              <Route path="/mypage/withdrawal" element={<MyWithdrawal />} />

              <Route path="/mypage/edit" element={<EditPage />} />
              <Route path="/mypage" element={<MyPage />}>
                <Route
                  path="/mypage"
                  element={<Navigate replace to="/mypage/history" />}
                />
                <Route path="/mypage/history" element={<HistoryPage />} />

                <Route path="/mypage/like" element={<LikePage />} />
              </Route>

              <Route path="/event/:id" element={<EventDetailPage />} />
              <Route path="/event" element={<EventListPage />} />

              <Route path="/notice/:id" element={<NoticeDetailPage />} />
              <Route path="/notice" element={<NoticePage />} />

              <Route path="/main" element={<MainPage />} />
              <Route path="/alimtalk/action" element={<AlimtalkActionPage />} />
              <Route path="/" element={<HomePage />} />
            </Routes>
          </Suspense>
        </main>
        <div className="mt-20">
          <Footer />
        </div>
      </div>
    </>
  );
};
